/**
 * The MUI theme, built entirely from `tokens.ts`.
 *
 * Nothing in this file invents a value. Every number and color traces back to a
 * token, so an M2 -> M3 swap is still a single-file edit (brainstorm.md 1.10).
 *
 * Components should read from this theme (`theme.palette.*`, `theme.spacing()`,
 * `theme.shape.borderRadius`) rather than importing tokens directly, because
 * the theme resolves light/dark for them.
 */
import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';

import {
  darkColors,
  fontFamily,
  fontSize,
  fontWeight,
  letterSpacing,
  lineHeight,
  lightColors,
  motion,
  opacity,
  radius,
  shadow,
  shadowDark,
  size,
  spacingUnit,
  tokens,
  type ColorScheme,
  type ThemeMode,
} from './tokens';

/** Font sizes are authored in px but emitted as rem so browser zoom works. */
const BASE_FONT_PX = 16;
const rem = (px: number) => `${px / BASE_FONT_PX}rem`;

/**
 * MUI wants a 25-entry shadow array. Our five elevation tokens are mapped onto
 * it: everything above the tokens we actually use repeats the largest one,
 * which keeps stray `elevation={14}` from inventing an untokenized shadow.
 */
function shadowScale(mode: ThemeMode): string[] {
  const s = mode === 'dark' ? shadowDark : shadow;
  const scale = [s.none, s.sm, s.sm, s.md, s.md, s.lg, s.lg, s.lg, s.xl, s.xl, s.xl, s.xxl];
  return Array.from({ length: 25 }, (_, i) => scale[i] ?? s.xxl);
}

function paletteFor(mode: ThemeMode, c: ColorScheme): ThemeOptions['palette'] {
  return {
    mode,
    primary: {
      main: c.accent,
      dark: c.accentPressed,
      light: c.accentHover,
      contrastText: c.onAccent,
    },
    secondary: { main: c.textSecondary, contrastText: c.surface },
    success: { main: c.success, contrastText: c.onAccent },
    warning: { main: c.warning, contrastText: c.textPrimary },
    error: { main: c.error, contrastText: c.onAccent },
    info: { main: c.info, contrastText: c.onAccent },
    background: { default: c.background, paper: c.surface },
    text: { primary: c.textPrimary, secondary: c.textSecondary, disabled: c.textDisabled },
    divider: c.divider,
    action: {
      hoverOpacity: opacity.hover,
      selectedOpacity: opacity.selected,
      focusOpacity: opacity.focus,
      disabledOpacity: opacity.disabled,
    },
  };
}

const typography: ThemeOptions['typography'] = {
  fontFamily: fontFamily.base,
  fontSize: fontSize.base,
  fontWeightLight: fontWeight.light,
  fontWeightRegular: fontWeight.regular,
  fontWeightMedium: fontWeight.medium,
  fontWeightBold: fontWeight.bold,

  h1: {
    fontSize: rem(fontSize.display),
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.tight,
  },
  h2: {
    fontSize: rem(fontSize.xxxl),
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.tight,
  },
  h3: {
    fontSize: rem(fontSize.xxl),
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.snug,
  },
  h4: {
    fontSize: rem(fontSize.xl),
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.snug,
  },
  h5: {
    fontSize: rem(fontSize.lg),
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.snug,
  },
  h6: {
    fontSize: rem(fontSize.md),
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.snug,
  },
  subtitle1: {
    fontSize: rem(fontSize.md),
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
  },
  subtitle2: {
    fontSize: rem(fontSize.base),
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.normal,
  },
  body1: {
    fontSize: rem(fontSize.base),
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
  },
  body2: {
    fontSize: rem(fontSize.sm),
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
  },
  caption: {
    fontSize: rem(fontSize.sm),
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
  },
  overline: {
    fontSize: rem(fontSize.xs),
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase',
  },
  button: {
    fontSize: rem(fontSize.base),
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.wide,
    // Classroom's buttons are sentence case, not the M2 default ALL CAPS.
    textTransform: 'none',
  },
};

function componentsFor(c: ColorScheme, mode: ThemeMode): ThemeOptions['components'] {
  const s = mode === 'dark' ? shadowDark : shadow;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': { boxSizing: 'border-box' },
        html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
        body: {
          backgroundColor: c.background,
          color: c.textPrimary,
          margin: 0,
        },
        // Long Canvas-authored HTML frequently contains oversized media.
        'img, video': { maxWidth: '100%', height: 'auto' },
        // Respect the OS setting. Motion is decoration, never the only signal.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
          },
        },
        ':focus-visible': {
          outline: `${size.focusRingWidth}px solid ${c.accent}`,
          outlineOffset: `${size.borderWidth}px`,
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: c.chrome,
          color: c.textPrimary,
          borderBottom: `${size.borderWidth}px solid ${c.divider}`,
          backgroundImage: 'none',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: c.chrome,
          borderRight: `${size.borderWidth}px solid ${c.divider}`,
          backgroundImage: 'none',
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: radius.md },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          border: `${size.borderWidth}px solid ${c.border}`,
          backgroundColor: c.surface,
          transition: `box-shadow ${motion.duration.medium}ms ${motion.easing.standard}`,
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: radius.sm, paddingInline: tokens.space.base },
        contained: { '&:hover': { boxShadow: s.sm } },
        outlined: { borderColor: c.border },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radius.pill, color: c.textSecondary },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.sm, fontWeight: fontWeight.medium },
        // A pill variant is available via `sx` where a Classroom-style
        // status chip is wanted.
      },
    },

    MuiTooltip: {
      defaultProps: { arrow: false },
      styleOverrides: {
        tooltip: {
          borderRadius: radius.sm,
          fontSize: rem(fontSize.sm),
          backgroundColor: mode === 'dark' ? c.surfaceVariant : c.textPrimary,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: radius.sm, boxShadow: s.lg, minWidth: size.drawerWidthCollapsed * 2 },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: radius.lg, boxShadow: s.xl },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          '&.Mui-selected': {
            backgroundColor: c.accentSubtle,
            color: c.accent,
            '& .MuiListItemIcon-root': { color: c.accent },
            '&:hover': { backgroundColor: c.accentSubtle },
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: tokens.space.xxl - tokens.space.sm, color: c.textSecondary } },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: fontWeight.medium,
          fontSize: rem(fontSize.base),
          minHeight: size.appBarHeightMobile,
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: size.appBarHeightMobile },
        indicator: { height: size.focusRingWidth + 1, borderRadius: radius.sm },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: c.divider } },
    },

    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: { backgroundColor: c.skeleton, borderRadius: radius.sm },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: c.divider,
          fontSize: rem(fontSize.base),
        },
        head: { fontWeight: fontWeight.medium, color: c.textSecondary },
      },
    },

    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: radius.pill } },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: radius.sm },
        notchedOutline: { borderColor: c.border },
      },
    },

    MuiAlert: {
      styleOverrides: { root: { borderRadius: radius.sm } },
    },

    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { color: c.accent } },
    },
  };
}

/** Build the full MUI theme for a mode. Memoized by `ThemeProvider`. */
export function buildTheme(mode: ThemeMode): Theme {
  const c = mode === 'dark' ? darkColors : lightColors;

  return createTheme({
    palette: paletteFor(mode, c),
    spacing: spacingUnit,
    shape: { borderRadius: radius.sm },
    breakpoints: { values: tokens.breakpoints },
    zIndex: tokens.zIndex,
    shadows: shadowScale(mode) as Theme['shadows'],
    typography,
    transitions: {
      duration: {
        shortest: motion.duration.instant,
        shorter: motion.duration.fast,
        short: motion.duration.medium,
        standard: motion.duration.medium,
        complex: motion.duration.slow,
        enteringScreen: motion.duration.medium,
        leavingScreen: motion.duration.fast,
      },
      easing: {
        easeInOut: motion.easing.standard,
        easeOut: motion.easing.decelerate,
        easeIn: motion.easing.accelerate,
        sharp: motion.easing.sharp,
      },
    },
    components: componentsFor(c, mode),
  });
}

export const lightTheme = buildTheme('light');
export const darkTheme = buildTheme('dark');
